/**
 * RoadMonks Collection Page JS
 * Handles AJAX filtering, double-range price slider, grid/list toggle,
 * expandable description, loading states, and wishlist/compare placeholders.
 */

(function () {
  'use strict';

  // Global references
  let isFetching = false;

  // Initialize all collection page functionality
  function initCollectionPage() {
    initDoublePriceSlider();
    initReadMoreDescription();
    initMobileDrawer();
    initViewPreference();
    initAccessibilityKeyboard();
    initCompareHandler();
    initWishlistHandler();
    initQuickViewHandler();
  }

  /* ========================================================
     AJAX FILTERING & HISTORY
  ======================================================== */
  function updateCollectionFilters() {
    if (isFetching) return;

    const form = document.getElementById('CollectionFiltersForm');
    if (!form) return;

    // Check if AJAX is enabled via settings (checking a data-attribute on the form or grid)
    const ajaxEnabled = form.dataset.ajaxEnabled === 'true';
    if (!ajaxEnabled) {
      form.submit();
      return;
    }

    isFetching = true;

    // Capture the submitted filter state BEFORE disabling any inputs.
    // FormData omits disabled form controls, so building it after disabling
    // would silently strip every checkbox/hidden field from the request.
    const formData = new FormData(form);
    const queryParams = new URLSearchParams(formData).toString();
    const url = `${window.location.pathname}?${queryParams}`;

    // Set loading state
    const grid = document.getElementById('collection-product-grid');
    if (grid) {
      grid.parentElement.classList.add('is-loading');
      
      // Inject skeleton loader cards
      const productsPerRow = parseInt(grid.dataset.productsPerRow || '3', 10);
      const isListView = grid.classList.contains('list-view');
      const cardColClass = isListView ? 'col-12' : `col-lg-${12 / productsPerRow} col-md-6 col-6`;
      
      let skeletonsHtml = '';
      const count = parseInt(grid.dataset.productsPerPage || '12', 10);
      for (let i = 0; i < count; i++) {
        skeletonsHtml += `
          <div class="${cardColClass} skeleton-card-col">
            <div class="skeleton-card">
              <div class="skeleton-image placeholder-shimmer"></div>
              <div class="skeleton-info">
                <div class="skeleton-line skeleton-brand placeholder-shimmer"></div>
                <div class="skeleton-line skeleton-title placeholder-shimmer"></div>
                <div class="skeleton-line skeleton-price placeholder-shimmer"></div>
                <div class="skeleton-line skeleton-emi placeholder-shimmer"></div>
              </div>
            </div>
          </div>
        `;
      }
      grid.innerHTML = skeletonsHtml;
    }

    // Disable all inputs to prevent double requests (safe now that formData/url are already captured)
    form.querySelectorAll('input, select, button').forEach(el => el.disabled = true);

    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response error');
        return response.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // 1. Swap product grid contents
        const newGrid = doc.getElementById('collection-product-grid');
        if (grid && newGrid) {
          grid.innerHTML = newGrid.innerHTML;
          // Retain list-view class if active
          const currentView = localStorage.getItem('roadmonks-collection-view') || 'grid';
          if (currentView === 'list') {
            grid.classList.add('list-view');
          } else {
            grid.classList.remove('list-view');
          }
        }

        // 2. Swap filter form contents (keeps disabled states and updated counts)
        // The mobile filter drawer (#filter-sidebar) lives inside the form, so replacing
        // innerHTML wipes its "open" state. Capture and restore it across the swap.
        const currentSidebar = document.getElementById('filter-sidebar');
        const sidebarWasOpen = currentSidebar ? currentSidebar.classList.contains('open') : false;

        const newForm = doc.getElementById('CollectionFiltersForm');
        if (form && newForm) {
          form.innerHTML = newForm.innerHTML;
        }

        if (sidebarWasOpen) {
          const refreshedSidebar = document.getElementById('filter-sidebar');
          if (refreshedSidebar) {
            refreshedSidebar.classList.add('open');
            refreshedSidebar.setAttribute('aria-hidden', 'false');
          }
        }

        // 3. Swap active filter chips
        const activeChipsContainer = document.querySelector('.active-filters-chips-bar');
        const newActiveChips = doc.querySelector('.active-filters-chips-bar');
        if (activeChipsContainer) {
          if (newActiveChips) {
            activeChipsContainer.outerHTML = newActiveChips.outerHTML;
          } else {
            activeChipsContainer.style.display = 'none';
          }
        } else if (newActiveChips) {
          // If none existed but now we have chips, insert before grid form row
          const formRow = document.querySelector('.filter-form');
          if (formRow) {
            formRow.insertAdjacentHTML('beforebegin', newActiveChips.outerHTML);
          }
        }

        // 4. Swap page count badges (hero & topbar)
        const badgeCount = document.querySelector('.collection-hero .badge');
        const newBadgeCount = doc.querySelector('.collection-hero .badge');
        if (badgeCount && newBadgeCount) {
          badgeCount.innerHTML = newBadgeCount.innerHTML;
        }

        // 5. Update accessible screen reader live region
        const totalProducts = newGrid ? newGrid.children.length : 0;
        const liveRegion = document.getElementById('ProductCountAccessibility');
        if (liveRegion) {
          liveRegion.textContent = `${totalProducts} products loaded.`;
        }

        // 6. Push history state
        window.history.pushState({ path: url }, '', url);

        // Re-initialize page events & clean loading state
        initCollectionPage();
        animateNewProducts();
      })
      .catch(err => {
        console.error('AJAX Filter failed, falling back to reload.', err);
        window.location.href = url;
      })
      .finally(() => {
        isFetching = false;
        if (grid) {
          grid.parentElement.classList.remove('is-loading');
        }
      });
  }

  // Fade products in with staggered entries
  function animateNewProducts() {
    const grid = document.getElementById('collection-product-grid');
    if (!grid) return;
    const cards = grid.querySelectorAll('.shop-product-card');
    cards.forEach((card, i) => {
      setTimeout(() => {
        card.classList.add('reveal-animated');
      }, i * 50);
    });
  }

  /* ========================================================
     DOUBLE PRICE SLIDER LOGIC
  ======================================================== */
  function initDoublePriceSlider() {
    const minInput = document.getElementById('price-slider-min');
    const maxInput = document.getElementById('price-slider-max');
    const minNumberInput = document.getElementById('price-input-min');
    const maxNumberInput = document.getElementById('price-input-max');
    const minLabel = document.getElementById('price-slider-min-lbl');
    const maxLabel = document.getElementById('price-slider-max-lbl');
    const track = document.getElementById('price-slider-track');
    const minHidden = document.getElementById('Filter-Price-Min-Hidden');
    const maxHidden = document.getElementById('Filter-Price-Max-Hidden');

    if (!minInput || !maxInput || !track) return;

    const rangeMax = parseFloat(minInput.max) || 100000;
    const minGap = 0; // Set gap to 0 so users can enter/select exact values
    const currencySymbol = (minLabel && minLabel.dataset.currencySymbol) || '₹';

    function updateSlider() {
      let minVal = parseFloat(minInput.value);
      let maxVal = parseFloat(maxInput.value);

      // Prevent overlapping handles (min cannot exceed max)
      if (minVal > maxVal) {
        if (document.activeElement === minInput || document.activeElement === minNumberInput) {
          minInput.value = maxVal;
          minVal = maxVal;
        } else {
          maxInput.value = minVal;
          maxVal = minVal;
        }
      }

      // Calculate track positions
      const leftPct = (minVal / rangeMax) * 100;
      const rightPct = 100 - (maxVal / rangeMax) * 100;

      track.style.left = leftPct + '%';
      track.style.right = rightPct + '%';

      // Format currency label output
      if (minLabel) {
        minLabel.textContent = currencySymbol + Math.round(minVal).toLocaleString();
      }
      if (maxLabel) {
        maxLabel.textContent = currencySymbol + Math.round(maxVal).toLocaleString() + (maxVal >= rangeMax ? '+' : '');
      }

      // Sync numeric inputs in real-time, except when the user is typing in them
      if (minNumberInput && document.activeElement !== minNumberInput) {
        minNumberInput.value = Math.round(minVal);
      }
      if (maxNumberInput && document.activeElement !== maxNumberInput) {
        maxNumberInput.value = Math.round(maxVal);
      }

      // Sync attributes for screen readers
      minInput.setAttribute('aria-valuenow', Math.round(minVal));
      maxInput.setAttribute('aria-valuenow', Math.round(maxVal));
    }

    // Dynamic Z-Index stack switching depending on active control
    function adjustZIndex(activeSlider) {
      if (activeSlider === 'min') {
        minInput.style.zIndex = '10';
        maxInput.style.zIndex = '9';
      } else {
        maxInput.style.zIndex = '10';
        minInput.style.zIndex = '9';
      }
    }

    minInput.addEventListener('input', () => {
      adjustZIndex('min');
      updateSlider();
    });
    maxInput.addEventListener('input', () => {
      adjustZIndex('max');
      updateSlider();
    });

    minInput.addEventListener('change', () => {
      if (minHidden) minHidden.value = Math.round(minInput.value);
      updateCollectionFilters();
    });
    maxInput.addEventListener('change', () => {
      if (maxHidden) maxHidden.value = Math.round(maxInput.value);
      updateCollectionFilters();
    });

    // Event listeners for numeric text inputs
    if (minNumberInput) {
      minNumberInput.addEventListener('input', () => {
        const val = parseFloat(minNumberInput.value);
        if (!isNaN(val)) {
          // Reposition handle and track in real-time, but don't clamp min/max yet
          minInput.value = val;
          updateSlider();
        }
      });
      minNumberInput.addEventListener('change', () => {
        validateAndFilterInputs();
      });
      minNumberInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          minNumberInput.blur();
        }
      });
    }

    if (maxNumberInput) {
      maxNumberInput.addEventListener('input', () => {
        const val = parseFloat(maxNumberInput.value);
        if (!isNaN(val)) {
          // Reposition handle and track in real-time, but don't clamp min/max yet
          maxInput.value = val;
          updateSlider();
        }
      });
      maxNumberInput.addEventListener('change', () => {
        validateAndFilterInputs();
      });
      maxNumberInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          maxNumberInput.blur();
        }
      });
    }

    // Run input validation/clamping, commit values, and trigger AJAX filter
    function validateAndFilterInputs() {
      let minVal = parseFloat(minNumberInput ? minNumberInput.value : minInput.value);
      let maxVal = parseFloat(maxNumberInput ? maxNumberInput.value : maxInput.value);

      if (isNaN(minVal)) minVal = 0;
      if (isNaN(maxVal)) maxVal = rangeMax;

      // Clamp within available price limits [0, rangeMax]
      if (minVal < 0) minVal = 0;
      if (minVal > rangeMax) minVal = rangeMax;
      if (maxVal < 0) maxVal = 0;
      if (maxVal > rangeMax) maxVal = rangeMax;

      // Ensure min <= max
      if (minVal > maxVal) {
        minVal = maxVal;
      }

      // Sync ranges
      minInput.value = minVal;
      maxInput.value = maxVal;

      updateSlider();

      // Sync form hidden inputs
      if (minHidden) minHidden.value = Math.round(minVal);
      if (maxHidden) maxHidden.value = Math.round(maxVal);

      updateCollectionFilters();
    }

    // Touch Support for mobile range overlapping selection
    minInput.addEventListener('touchstart', () => adjustZIndex('min'), { passive: true });
    maxInput.addEventListener('touchstart', () => adjustZIndex('max'), { passive: true });

    updateSlider();
  }

  /* ========================================================
     EXPANDABLE DESCRIPTION (Read More / Read Less)
  ======================================================== */
  function initReadMoreDescription() {
    const descText = document.getElementById('collection-desc-text');
    const toggleBtn = document.getElementById('desc-toggle-btn');
    if (!descText || !toggleBtn) return;

    // Reset styles for calculation
    descText.classList.remove('expanded');
    descText.style.maxHeight = '';
    
    // Inject bottom fade transition element
    let fadeOverlay = descText.querySelector('.collection-desc-fade');
    if (!fadeOverlay) {
      fadeOverlay = document.createElement('div');
      fadeOverlay.className = 'collection-desc-fade';
      descText.appendChild(fadeOverlay);
    }

    // Check height to determine overflow
    const collapsedHeight = descText.clientHeight;
    const totalHeight = descText.scrollHeight;

    if (totalHeight > collapsedHeight + 5) {
      toggleBtn.style.display = 'inline-block';
      descText.style.maxHeight = '4.8em';
    } else {
      toggleBtn.style.display = 'none';
      fadeOverlay.style.opacity = '0';
    }

    toggleBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (descText.classList.contains('expanded')) {
        descText.style.maxHeight = '4.8em';
        descText.classList.remove('expanded');
        toggleBtn.textContent = 'Read More';
        toggleBtn.setAttribute('aria-expanded', 'false');
      } else {
        descText.style.maxHeight = totalHeight + 'px';
        descText.classList.add('expanded');
        toggleBtn.textContent = 'Read Less';
        toggleBtn.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /* ========================================================
     MOBILE SLIDE-OUT DRAWER
  ======================================================== */
  function initMobileDrawer() {
    const toggleSidebarBtn = document.getElementById('filter-sidebar-toggle');
    const sidebar = document.getElementById('filter-sidebar');
    const overlayBg = document.getElementById('filter-overlay-bg');
    const closeSidebarBtn = document.getElementById('filter-sidebar-close');

    if (!toggleSidebarBtn || !sidebar || !overlayBg) return;

    toggleSidebarBtn.addEventListener('click', function (e) {
      e.preventDefault();
      sidebar.classList.add('open');
      sidebar.setAttribute('aria-hidden', 'false');
      overlayBg.classList.add('show');
      document.body.style.overflow = 'hidden';
      // Focus on close button inside mobile drawer
      setTimeout(() => closeSidebarBtn?.focus(), 150);
    });

    function closeSidebarDrawer() {
      sidebar.classList.remove('open');
      sidebar.setAttribute('aria-hidden', 'true');
      overlayBg.classList.remove('show');
      document.body.style.overflow = '';
      toggleSidebarBtn.focus();
    }

    closeSidebarBtn?.addEventListener('click', closeSidebarDrawer);
    overlayBg.addEventListener('click', closeSidebarDrawer);
  }

  /* ========================================================
     GRID / LIST VIEW PREFERENCE
  ======================================================== */
  function initViewPreference() {
    const grid = document.getElementById('collection-product-grid');
    if (!grid) return;

    const viewIcons = document.querySelectorAll('.view-icon');
    if (!viewIcons.length) return;

    // Load user preferred view
    const savedView = localStorage.getItem('roadmonks-collection-view') || 'grid';
    setView(savedView);

    viewIcons.forEach(icon => {
      icon.addEventListener('click', () => {
        const view = icon.dataset.view;
        localStorage.setItem('roadmonks-collection-view', view);
        setView(view);
      });
    });

    function setView(view) {
      viewIcons.forEach(i => {
        if (i.dataset.view === view) {
          i.classList.add('active');
        } else {
          i.classList.remove('active');
        }
      });

      if (view === 'list') {
        grid.classList.add('list-view');
      } else {
        grid.classList.remove('list-view');
      }
    }
  }

  /* ========================================================
     KEYBOARD ACCESSIBILITY TOGGLES
  ======================================================== */
  function initAccessibilityKeyboard() {
    // Keyboard Space/Enter keys for collapsible side sections
    document.querySelectorAll('.filter-section-title[role="button"]').forEach(title => {
      title.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          this.click();
        }
      });
    });
  }

  /* ========================================================
     WISHLIST PLACEHOLDER HANDLER
  ======================================================== */
  function initWishlistHandler() {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const productId = btn.dataset.productId;
      if (!productId) return;

      // Check client wishlist cache
      const wishlist = JSON.parse(localStorage.getItem('roadmonks-wishlist') || '[]');
      if (wishlist.includes(productId)) {
        btn.classList.add('in-wishlist');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const currentWishlist = JSON.parse(localStorage.getItem('roadmonks-wishlist') || '[]');
        const idx = currentWishlist.indexOf(productId);

        if (idx > -1) {
          currentWishlist.splice(idx, 1);
          btn.classList.remove('in-wishlist');
          btn.setAttribute('aria-label', 'Add to wishlist');
        } else {
          currentWishlist.push(productId);
          btn.classList.add('in-wishlist');
          btn.setAttribute('aria-label', 'Remove from wishlist');
        }

        localStorage.setItem('roadmonks-wishlist', JSON.stringify(currentWishlist));
      });
    });
  }

  /* ========================================================
     QUICK VIEW HOVER DIALOGUE HANDLER
  ======================================================== */
  function initQuickViewHandler() {
    document.querySelectorAll('.quick-view-btn').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const url = this.dataset.productUrl;
        if (url) {
          // Open quick view modal dynamically (or redirect to product page if no modal present)
          // Since it's a placeholder, we alert or display custom notification for premium touch
          alert(`Quick View Loading details for: ${url.split('/').pop()}`);
        }
      });
    });
  }

  /* ========================================================
     CLIENT-SIDE PRODUCT COMPARISON
  ======================================================== */
  function initCompareHandler() {
    const compareSwitch = document.getElementById('compareProductsSwitch');
    if (!compareSwitch) return;

    const cardsCompareList = document.querySelectorAll('.card-compare-wrapper');

    compareSwitch.addEventListener('change', function () {
      const active = compareSwitch.checked;
      if (active) {
        cardsCompareList.forEach(wrapper => wrapper.classList.remove('d-none'));
      } else {
        cardsCompareList.forEach(wrapper => wrapper.classList.add('d-none'));
        hideCompareDrawer();
      }
    });

    // Checkbox click listener
    document.querySelectorAll('.card-compare-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', function () {
        updateCompareDrawer();
      });
    });

    function updateCompareDrawer() {
      const checkedBoxes = document.querySelectorAll('.card-compare-checkbox:checked');
      if (checkedBoxes.length === 0) {
        hideCompareDrawer();
        return;
      }

      let drawer = document.getElementById('compare-products-drawer');
      if (!drawer) {
        drawer = document.createElement('div');
        drawer.id = 'compare-products-drawer';
        drawer.className = 'compare-drawer p-3';
        document.body.appendChild(drawer);
      }

      drawer.innerHTML = `
        <div class="compare-drawer-header d-flex justify-content-between align-items-center mb-2 px-1">
          <span class="fw-bold fs-8 text-uppercase text-dark"><i class="fas fa-compress-alt me-1 text-primary"></i> Comparison (${checkedBoxes.length} Selected)</span>
          <button class="btn btn-sm btn-link p-0 text-decoration-none text-muted" id="clear-compare-btn" style="font-size:0.75rem;">Clear All</button>
        </div>
        <div class="compare-drawer-items d-flex gap-3 overflow-auto py-1 mb-2">
          ${Array.from(checkedBoxes).map(box => `
            <div class="compare-item d-flex align-items-center gap-2 border rounded-pill px-2 py-1 bg-white" style="font-size:0.75rem; white-space:nowrap;">
              <img src="${box.dataset.productImg}" alt="${box.dataset.productTitle}" style="width:24px; height:24px; object-fit:contain; border-radius:50%;">
              <span class="fw-bold text-truncate" style="max-width:100px;">${box.dataset.productTitle}</span>
              <button class="btn-close remove-compare-item" data-id="${box.dataset.productId}" aria-label="Remove item" style="font-size:0.5rem;"></button>
            </div>
          `).join('')}
        </div>
        <div class="compare-actions d-flex gap-2">
          <button class="btn btn-dark btn-sm rounded-pill w-100 fw-bold text-uppercase fs-9 py-2" id="compare-trigger-modal">Compare Products</button>
        </div>
      `;

      drawer.classList.add('show');

      // Bind events inside drawer
      document.getElementById('clear-compare-btn')?.addEventListener('click', () => {
        document.querySelectorAll('.card-compare-checkbox:checked').forEach(box => box.checked = false);
        hideCompareDrawer();
      });

      drawer.querySelectorAll('.remove-compare-item').forEach(btn => {
        btn.addEventListener('click', function () {
          const id = this.dataset.id;
          const matchedBox = document.getElementById(`Compare-${id}`);
          if (matchedBox) {
            matchedBox.checked = false;
            updateCompareDrawer();
          }
        });
      });

      document.getElementById('compare-trigger-modal')?.addEventListener('click', showCompareModal);
    }

    function hideCompareDrawer() {
      const drawer = document.getElementById('compare-products-drawer');
      if (drawer) drawer.classList.remove('show');
    }

    function showCompareModal() {
      const checkedBoxes = document.querySelectorAll('.card-compare-checkbox:checked');
      if (checkedBoxes.length < 2) {
        alert('Please select at least 2 products to compare.');
        return;
      }

      let modal = document.getElementById('compare-products-modal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'compare-products-modal';
        modal.className = 'modal fade';
        modal.setAttribute('tabindex', '-1');
        modal.setAttribute('aria-hidden', 'true');
        document.body.appendChild(modal);
      }

      modal.innerHTML = `
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content border-0 rounded-4 shadow-lg">
            <div class="modal-header bg-dark text-white border-0 rounded-top-4 p-3">
              <h5 class="modal-title fw-bold text-uppercase fs-7"><i class="fas fa-compress-alt me-2 text-primary"></i> Product Comparison</h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body p-4 overflow-auto">
              <table class="table table-bordered mb-0 align-middle">
                <thead>
                  <tr class="table-light">
                    <th scope="col" class="fw-black text-uppercase text-muted fs-9" style="width:25%;">Features</th>
                    ${Array.from(checkedBoxes).map(box => `
                      <th scope="col" class="text-center" style="width:${75 / checkedBoxes.length}%;">
                        <img src="${box.dataset.productImg}" alt="${box.dataset.productTitle}" class="img-fluid mb-2" style="max-height:80px; object-fit:contain;">
                        <div class="fw-bold text-dark text-uppercase fs-8">${box.dataset.productTitle}</div>
                      </th>
                    `).join('')}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="fw-bold text-uppercase fs-9">Brand</td>
                    ${Array.from(checkedBoxes).map(box => `
                      <td class="text-center text-secondary fs-8">${box.dataset.productVendor}</td>
                    `).join('')}
                  </tr>
                  <tr>
                    <td class="fw-bold text-uppercase fs-9">Price</td>
                    ${Array.from(checkedBoxes).map(box => `
                      <td class="text-center fw-bold text-dark fs-7">${box.dataset.productPrice}</td>
                    `).join('')}
                  </tr>
                  <tr>
                    <td class="fw-bold text-uppercase fs-9">Actions</td>
                    ${Array.from(checkedBoxes).map(box => `
                      <td class="text-center">
                        <a href="${box.dataset.productUrl}" class="btn btn-sm btn-dark rounded-pill text-uppercase fw-bold fs-9 px-3">View Details</a>
                      </td>
                    `).join('')}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;

      const bsModal = new bootstrap.Modal(modal);
      bsModal.show();
    }
  }

  // Bind forms and initial loads
  document.addEventListener('DOMContentLoaded', () => {
    initCollectionPage();
    animateNewProducts();

    // Auto submit form on checking facet filters via AJAX
    document.addEventListener('change', function (e) {
      if (e.target.classList.contains('filter-checkbox')) {
        updateCollectionFilters();
      }
    });

    // Listen to sort-by selections
    document.addEventListener('change', function (e) {
      const sortBy = e.target.closest('#sort-by');
      if (sortBy) {
        const filterForm = document.getElementById('CollectionFiltersForm');
        if (filterForm) {
          let sortHiddenInput = filterForm.querySelector('input[name="sort_by"]');
          if (!sortHiddenInput) {
            sortHiddenInput = document.createElement('input');
            sortHiddenInput.type = 'hidden';
            sortHiddenInput.name = 'sort_by';
            filterForm.appendChild(sortHiddenInput);
          }
          sortHiddenInput.value = sortBy.value;
          updateCollectionFilters();
        }
      }
    });

    // Dynamic browser back/forward buttons AJAX sync
    window.addEventListener('popstate', () => {
      window.location.reload(); // Simplest robust back button state handler
    });
  });

})();
